import React from 'react';

import type { GameSourceRecord } from '../../game-sources';
import { useGameThumb } from '../../useGameThumb';

export interface GameThumbProps {
  /** The game to draw. A slim catalogue record is enough: its art is read by id (see `useGameThumb`). */
  game: GameSourceRecord | undefined;
  /** Draws the thumbnail once its URL is known, e.g. `(src) => <S.ArtImg src={src} alt="" loading="lazy" />`. */
  render: (src: string) => React.ReactNode;
  /** Shown while the art is being looked up. Nothing by default; a game that turns out to have no art shows nothing. */
  placeholder?: React.ReactNode;
}

/**
 * A game's thumbnail that loads itself, for a render function that cannot call hooks (a list's row renderer). It
 * draws the game's own art when the record carries it, and otherwise asks for the game's full record by id (batched
 * with every other thumbnail drawn in the same render), showing `placeholder` until the art is known. No markup of
 * its own: what it draws is the caller's. In a component, `useGameThumb` gives the same answer as a value.
 */
export const GameThumb = ({ game, render, placeholder = null }: GameThumbProps): React.ReactElement | null => {
  const { src, loading } = useGameThumb(game);
  if (src !== undefined) return <>{render(src)}</>;
  return loading ? <>{placeholder}</> : null;
};

export default GameThumb;
